import type { UseMutateAsyncFunction } from '@tanstack/react-query';

import type {
  SaveOfferBuilderRoomParams,
  SavedOfferBuilderRoom,
} from '~/hooks/apis/accommodation/useSaveOfferBuilderRoom';

import getErrorMessage from '~/utils/getErrorMessage';

import { buildRoomCreatePayload, getCreatableRoomDrafts } from './utils';
import type { FlashOfferRoomDraft } from './utils';

export interface CreateRoomsParams {
  hasInvalidRoomDraft: boolean;
  hasRoomDraftFormError: boolean;
  missingRequiredRoomFields: Array<string>;
  offerId: string;
  propertyId: string | undefined;
  roomDrafts: Array<FlashOfferRoomDraft>;
  saveRoom: UseMutateAsyncFunction<SavedOfferBuilderRoom, unknown, SaveOfferBuilderRoomParams>;
}

export interface CreateRoomsResult {
  errorMessage?: string;
  savedRoomsByDraftId: Map<string, SavedOfferBuilderRoom>;
  success: boolean;
}

/**
 * Creates each creatable room draft in order, stopping at the first failure; skipped drafts match
 * what the review step showed. Callers own applying the result to their own state.
 */
export async function createRooms({
  hasInvalidRoomDraft,
  hasRoomDraftFormError,
  missingRequiredRoomFields,
  offerId,
  propertyId,
  roomDrafts,
  saveRoom,
}: CreateRoomsParams): Promise<CreateRoomsResult> {
  const savedRoomsByDraftId = new Map<string, SavedOfferBuilderRoom>();

  if (!propertyId) {
    return {
      errorMessage: 'Select or create a property before creating rooms.',
      savedRoomsByDraftId,
      success: false,
    };
  }

  if (hasRoomDraftFormError) {
    return { errorMessage: 'Unable to load room fields.', savedRoomsByDraftId, success: false };
  }

  if (hasInvalidRoomDraft) {
    return {
      errorMessage: `Complete mandatory room fields: ${missingRequiredRoomFields.join(', ') || 'Name'}.`,
      savedRoomsByDraftId,
      success: false,
    };
  }

  const roomDraftsToCreate = getCreatableRoomDrafts(roomDrafts);

  if (roomDraftsToCreate.length === 0) {
    return { savedRoomsByDraftId, success: true };
  }

  try {
    for (const roomDraft of roomDraftsToCreate) {
      const savedRoom = await saveRoom({
        offerId,
        propertyId,
        room: buildRoomCreatePayload(roomDraft, propertyId),
        suppressNotifications: true,
      });
      savedRoomsByDraftId.set(roomDraft.draftId, savedRoom);
    }

    return { savedRoomsByDraftId, success: true };
  } catch (error) {
    return {
      errorMessage: `Unable to create rooms. ${getErrorMessage(error)}`,
      savedRoomsByDraftId,
      success: false,
    };
  }
}
